import { useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Pagination from "../../components/common/Pagination";
import Table from "../../components/common/Table";
import ActionButton from "../../components/common/ActionButton";
import { useStore } from "../../context/StoreContext";
import useClientPagination from "../../hooks/useClientPagination";
import {
  useGetTagsQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
} from "../../store/api";

const Tags = () => {
  const { selectedStoreId } = useStore();
  const { data, isLoading, isError, error } = useGetTagsQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });
  const [createTag, createState] = useCreateTagMutation();
  const [updateTag, updateState] = useUpdateTagMutation();
  const [deleteTag, deleteTagState] = useDeleteTagMutation();

  const [form, setForm] = useState({
    name: "",
    code: "",
    tagType: "DIETARY",
    description: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    tagId: null,
    tagName: "",
  });

  const tags = Array.isArray(data) ? data : [];
  const {
    page,
    totalPages,
    totalCount,
    pageSize,
    paginatedItems: paginatedTags,
    setPage,
    setPageSize,
  } = useClientPagination(tags, { pageSize: 10 });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStoreId) {
      alert("Please select a store first");
      return;
    }
    try {
      if (editingId) {
        await updateTag({
          storeId: selectedStoreId,
          tagId: editingId,
          payload: form,
        }).unwrap();
        setEditingId(null);
      } else {
        await createTag({ storeId: selectedStoreId, payload: form }).unwrap();
      }
      setForm({
        name: "",
        code: "",
        tagType: "DIETARY",
        description: "",
      });
      setShowForm(false);
    } catch (err) {
      // error handled by UI
    }
  };

  const handleEdit = (tag) => {
    setEditingId(tag.tagId || tag.id);
    setForm({
      name: tag.name || tag.tagName || "",
      code: tag.code || "",
      tagType: tag.tagType || "DIETARY",
      description: tag.description || "",
    });
    setShowForm(true);
  };

  const handleDelete = (tag) => {
    setDeleteModal({
      isOpen: true,
      tagId: tag.tagId || tag.id,
      tagName: tag.name || tag.tagName || "this tag",
    });
  };

  const confirmDelete = async () => {
    try {
      await deleteTag({
        storeId: selectedStoreId,
        tagId: deleteModal.tagId,
      }).unwrap();
    } catch (err) {
      // error handled by UI
    } finally {
      setDeleteModal({
        isOpen: false,
        tagId: null,
        tagName: "",
      });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm({
      name: "",
      code: "",
      tagType: "DIETARY",
      description: "",
    });
    setShowForm(false);
  };

  const columns = [
    { key: "name", label: "Name" },
    { key: "code", label: "Code" },
    { key: "tagType", label: "Tag Type" },
    // { key: "description", label: "Description" },
    { key: "actions", label: "Actions" },
  ];

  const rows = paginatedTags.map((tag) => {
    const id = tag.tagId || tag.id;
    return {
      id,
      name: tag.name || tag.tagName,
      code: tag.code || "-",
      tagType: tag.tagType || "-",
      description: tag.description || "-",
      actions: (
        <div className="flex gap-3">
          <ActionButton
            label="Edit"
            icon="edit"
            onClick={() => handleEdit(tag)}
          />
          <ActionButton
            label="Delete"
            icon="delete"
            tone="danger"
            onClick={() => handleDelete(tag)}
          />
        </div>
      ),
    };
  });

  if (!selectedStoreId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Tags"
          subtitle="Manage custom tags for menu items."
        />
        <Card>
          <Alert tone="warning">Please select a store to manage tags.</Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tags"
        subtitle="Manage custom tags for menu items."
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button size="sm" onClick={() => setShowForm(true)}>
              Add Tag
            </Button>
          )
        }
      />
      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900">
            {editingId ? "Edit Tag" : "Add Tag"}
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Name"
                name="name"
                placeholder="Spicy"
                value={form.name}
                onChange={handleChange}
                required
              />
              <Input
                label="Code"
                name="code"
                placeholder="SPICY"
                value={form.code}
                onChange={handleChange}
                helper="Required unique code used by the backend"
                required
              />
              <label className="flex flex-col gap-2 text-sm text-slate-600">
                Tag Type
                <select
                  name="tagType"
                  value={form.tagType}
                  onChange={handleChange}
                  className="h-11 rounded-lg border border-slate-200 bg-white px-4 text-base text-slate-900 shadow-sm outline-none transition focus:border-slate-400"
                  required
                >
                  <option value="DIETARY">Dietary</option>
                  <option value="ATTRIBUTE">Attribute</option>
                  <option value="PROMOTIONAL">Promotional</option>
                  <option value="CUSTOM">Custom</option>
                </select>
              </label>
              {/* <Input
                label="Description"
                name="description"
                placeholder="Contains spicy ingredients"
                value={form.description}
                onChange={handleChange}
              /> */}
            </div>

            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Tag {editingId ? "updated" : "created"} successfully.
              </Alert>
            )}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {createState.error?.data?.detail ||
                  updateState.error?.data?.detail ||
                  "Failed to save tag."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Tag
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <Card className="p-0">
          <div className="border-b border-slate-100 px-6 py-4 text-sm font-semibold text-slate-700">
            All Tags
          </div>
          <div className="px-6 py-4">
            {isLoading && (
              <p className="text-sm text-slate-500">Loading tags...</p>
            )}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load tags."}
              </Alert>
            )}
            {!isLoading && !isError && (
              <>
                <Table columns={columns} rows={rows} emptyText="No tags yet." />
                {totalPages > 1 && (
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              )}
              </>
            )}
          </div>
        </Card>
      )}

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({
            isOpen: false,
            tagId: null,
            tagName: "",
          })
        }
        title="Delete Tag"
        confirmText="Delete"
        onConfirm={confirmDelete}
        isLoading={deleteTagState.isLoading}
      >
        <p>
          Are you sure you want to delete <strong>{deleteModal.tagName}</strong>
          ?
        </p>
      </Modal>
    </div>
  );
};

export default Tags;
